<script setup>
import BreadCrumbs from '@/components/breadcrumbs/BreadCrumbs.vue'
import ConferenceCard from '@/components/conferences/ConferenceCard.vue'
import ContentSubheaderPage from '@/components/content/ContentSubheaderPage.vue'
import SearchField from '@/components/fields/SearchField.vue'
import CategoryFilters from '@/components/filters/CategoryFilters.vue'
import { useConferencesStore } from '@/store/conferences'
import { storeToRefs } from 'pinia'
import { onMounted, ref, watch, onBeforeUpdate, watchEffect } from 'vue'
import * as helper from '@/store/helpers'
import debounce from 'lodash.debounce'

const conferencesStore = useConferencesStore()
const { formatConferencesTags } = storeToRefs(conferencesStore)

const isMounted = ref(false)

const conferenceFilters = ref([])
const mobileConferenceFilters = ref([])
const selectedConferencesFilters = ref([])
const seminarsFilters = ref([])
const mobileSeminarsFilters = ref([])
const selectedSeminarsFilters = ref([])
const searchStr = ref('')
const isNewConferencesSorting = ref(true)

const conferencesCount = ref(0)
const conferenceCards = ref([])

const pageNumber = ref(1) // Текущая страница
const paginationCount = ref(15) // Количество новостей на 1 странице
const maxPage = ref(0)
const pageElems = ref([]) // ref-ссылки на dom-элементы (номера страниц)

const isConferenceDataValid = (conferenceData) => {
    if (
        conferenceData.text_header === null ||
        conferenceData.text_header.length === 0 ||
        conferenceData.creation_time === null
    ) {
        return false
    }

    return true
}

const getMaxPage = (conferencesCount) => {
    return Math.ceil(conferencesCount / paginationCount.value)
}

const changePage = (page) => {
    pageNumber.value = page

    for (let i = 0; i < pageElems.value.length; i++) {
        if (pageElems.value[i].classList.contains('toggle')) {
            pageElems.value[i].classList.remove('toggle')
            break
        }
    }

    if (pageElems.value.length > 1) {
        pageElems.value[page - 1].classList.add('toggle')
    }

    document
        .getElementById('all-conferences-page')
        .scrollIntoView({ behavior: 'smooth' }) // Прокрутка страницы
}

const getConferences = async (
    isEmpty = true,
    searchString = '',
    tagsId = [],
    isDesc = true
) => {
    if (isEmpty) {
        conferencesCount.value = await helper.api(
            'GET',
            '/conferences_and_seminars/count',
            {}
        )
        await conferencesStore.getConferences(
            0,
            paginationCount.value * pageNumber.value
        )
        conferenceCards.value = conferencesStore.allConferences
    } else {
        conferencesCount.value = await helper.api(
            'GET',
            '/conferences_and_seminars/count',
            {
                search_string: searchString,
                tags_id: tagsId
            }
        )
        await conferencesStore.getConferences(
            paginationCount.value * (pageNumber.value - 1),
            paginationCount.value * pageNumber.value,
            searchString,
            tagsId,
            isDesc
        )
        conferenceCards.value = conferencesStore.allConferences
    }
}

const changeSorting = () => {
    isNewConferencesSorting.value = !isNewConferencesSorting.value

    if (isNewConferencesSorting.value) {
        document.getElementById('conference-sort-arrow').style.transform =
            'rotate(0)'
        document.getElementById('conference-sort-arrow-mobile').style.transform =
            'rotate(0)'
    } else {
        document.getElementById('conference-sort-arrow').style.transform =
            'rotate(180deg)'
        document.getElementById('conference-sort-arrow-mobile').style.transform =
            'rotate(0)'
    }
}

const clearFilters = () => {
    searchStr.value = ''
    selectedConferencesFilters.value = []
    selectedSeminarsFilters.value = []
}

watch(
    [
        searchStr,
        selectedConferencesFilters,
        selectedSeminarsFilters,
        isNewConferencesSorting
    ],
    debounce(async () => {
        changePage(1)
        await getConferences(
            false,
            searchStr.value,
            [
                ...selectedConferencesFilters.value.flat(),
                ...selectedSeminarsFilters.value.flat()
            ],
            isNewConferencesSorting.value
        )
        maxPage.value = getMaxPage(conferencesCount.value)
    }, 500)
)

// Отслеживание переключения страниц
watch(pageNumber, async () => {
    await getConferences(
        false,
        searchStr.value,
        [...selectedConferencesFilters.value, ...selectedSeminarsFilters.value],
        isNewConferencesSorting.value
    )
})

watchEffect(() => {
  conferenceCards.value = conferenceCards.value.filter((conference) => isConferenceDataValid(conference))
})

onMounted(async () => {
    await conferencesStore.getConferencesTags(true)
    conferenceFilters.value = formatConferencesTags.value('Конференции', false)
    seminarsFilters.value = formatConferencesTags.value('Семинары', true)

    for (const filter of conferenceFilters.value) {
        for (const subFilter of filter.filters) {
            if (!subFilter.tagsId) {
                mobileConferenceFilters.value.push({
                    id: [subFilter.id],
                    title: subFilter.title
                })
            } else {
                mobileConferenceFilters.value.push({
                    id: subFilter.tagsId,
                    title: subFilter.title
                })
            }
        }
    }

    for (const filter of seminarsFilters.value) {
        if (!filter.tagsId) {
            mobileSeminarsFilters.value.push({
                id: [filter.id],
                title: filter.title
            })
        } else {
            mobileSeminarsFilters.value.push({
                id: filter.tagsId,
                title: filter.title
            })
        }
    }

    await getConferences()
    maxPage.value = getMaxPage(conferencesCount.value)

    isMounted.value = true // Карточка с фильтрами загружается раньше страницы, для избежания этого используется данный костыль
})

onBeforeUpdate(() => {
    pageElems.value = []
})
</script>

<template>
    <div
        id="all-conferences-page"
        class="container"
    >
        <bread-crumbs />
        <content-subheader-page
            class="all-conference-page-content-subheader"
            text="КОНФЕРЕНЦИИ И СЕМИНАРЫ"
        />
        <v-container
            :fluid="true"
            class="pa-0 all-conferences-container"
        >
            <v-row class="ml-n2 mr-n2">
                <v-col
                    class="pl-2 pr-2 d-none d-lg-block"
                    cols="3"
                >
                    <div class="filter-card">
                        <h2
                            v-if="conferenceFilters.length > 0"
                            class="filter-card-title"
                        >
                            Конференции
                        </h2>
                        <category-filters
                            v-if="isMounted"
                            :conference-filters="conferenceFilters"
                            :is-single="false"
                            @detect-select="
                                (selectedConferencesFiltersArg) => {
                                    selectedConferencesFilters =
                                        selectedConferencesFiltersArg
                                }
                            "
                        />
                        <h2
                            v-if="seminarsFilters.length > 0"
                            class="filter-card-title"
                        >
                            Семинары
                        </h2>
                        <category-filters
                            v-if="isMounted"
                            :conference-filters="seminarsFilters"
                            :is-single="true"
                            @detect-select="
                                (selectedSeminarsFiltersArg) => {
                                    selectedSeminarsFilters =
                                        selectedSeminarsFiltersArg
                                }
                            "
                        />
                    </div>
                </v-col>
                <v-col
                    class="pl-2 pr-2"
                    cols="12"
                    lg="9"
                >
                    <div class="sorting-field d-flex d-lg-none mb-6">
                        Сортировка:&nbsp;
                        <span @click="changeSorting">
                            по дате добавления
                            <img
                                id="conference-sort-arrow-mobile"
                                src="/svg/dropdown_arrow.svg"
                                alt="arrow"
                            />
                        </span>
                    </div>
                    <Search-field v-model="searchStr" />
                    <v-select
                        v-model="selectedConferencesFilters"
                        :items="mobileConferenceFilters"
                        class="d-lg-none"
                        label="Конференции"
                        variant="outlined"
                        item-title="title"
                        item-value="id"
                        multiple
                    />
                    <v-select
                        v-model="selectedSeminarsFilters"
                        :items="mobileSeminarsFilters"
                        class="d-lg-none"
                        label="Семинары"
                        variant="outlined"
                        item-title="title"
                        item-value="id"
                        multiple
                    />
                    <v-btn
                        class="text-none inputs-button d-lg-none"
                        height="60"
                        variant="flat"
                        color="#3B66D3"
                        @click="clearFilters"
                    >
                        Сбросить фильтры
                    </v-btn>
                    <div class="sorting-field d-none d-lg-flex">
                        Сортировка:&nbsp;
                        <span @click="changeSorting">
                            по дате добавления
                            <img
                                id="conference-sort-arrow"
                                src="/svg/dropdown_arrow.svg"
                                alt="arrow"
                            />
                        </span>
                    </div>
                    <v-row class="ml-n2 mr-n2 mt-7">
                        <v-col
                            v-for="card in conferenceCards"
                            :key="card.id"
                            class="pl-2 pr-2 pb-2 pt-2"
                            cols="12"
                            sm="6"
                            lg="4"
                        >
                            <ConferenceCard v-bind="card" />
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </v-container>
        <v-row class="ml-n2 mr-n2">
            <v-col
                class="pl-2 pr-2 ml-auto"
                cols="12"
                lg="9"
            >
                <v-container
                    :fluid="true"
                    class="pa-0 pagination"
                    :class="{ 'd-none': maxPage < 2 }"
                >
                    <div
                        class="page-arrows"
                        @click="changePage(1)"
                    >
                        <svg
                            width="20"
                            height="15"
                            viewBox="0 0 20 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10 1.75152L8.48329 0.5L0 7.5L8.48329 14.5L10 13.2485L3.03342 7.5L10 1.75152Z"
                                fill="#0076DE"
                            />
                            <path
                                d="M20 1.75152L18.4833 0.5L10 7.5L18.4833 14.5L20 13.2485L13.0334 7.5L20 1.75152Z"
                                fill="#0076DE"
                            />
                        </svg>
                    </div>
                    <div
                        v-for="(page, i) in maxPage"
                        v-show="
                            (pageNumber < 5
                                ? page <= 5
                                : page < pageNumber + 3) &&
                            (pageNumber < 5 ? true : page > pageNumber - 3)
                        "
                        :key="page"
                        :ref="
                            (el) => {
                                if (el) pageElems[i] = el
                            }
                        "
                        class="page"
                        :class="{ toggle: i === 0 }"
                        @click="changePage(page)"
                    >
                        {{ page }}
                    </div>
                    <div
                        class="page-arrows"
                        @click="changePage(maxPage)"
                    >
                        <svg
                            width="20"
                            height="15"
                            viewBox="0 0 20 15"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M10 13.2485L11.5167 14.5L20 7.5L11.5167 0.500001L10 1.75152L16.9666 7.5L10 13.2485Z"
                                fill="#0076DE"
                            />
                            <path
                                d="M0 13.2485L1.51671 14.5L10 7.5L1.51671 0.5L0 1.75152L6.96658 7.5L0 13.2485Z"
                                fill="#0076DE"
                            />
                        </svg>
                    </div>
                </v-container>
            </v-col>
        </v-row>
    </div>
</template>

<style lang="scss" scoped>
.container {
    margin-top: 32px;
    margin-left: 104px;
    margin-right: 104px;

    @media screen and (max-width: 767px) {
        margin-left: 40px;
        margin-right: 40px;
        margin-top: 24px;
    }
}

.all-conference-page-content-subheader {
    text-align: left;
}

.all-conferences-container {
    margin-top: 28px;
}

.filter-card {
    max-height: 1030px;
    border-radius: 5px;
    background: #fff;
    box-shadow: 1px 1px 50px 10px #2632380d;
    padding: 0 25px 20px 25px;
    display: flex;
    flex-direction: column;
    align-items: start;
    overflow-y: auto;
}

.filter-card-title {
    font-size: 28px;
    font-weight: 500;
    line-height: 36px;
    color: #0635ac;
    margin-top: 20px;
}

.sorting-field {
    display: flex;
    justify-content: left;

    span {
        display: flex;
        align-items: center;
        color: #0635ac;
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
            color: #144cd8;
        }
    }
}

.inputs-button {
    width: 100%;
    font-weight: 500;
    font-size: 20px;
    line-height: 28px;
}

.pagination {
    display: flex;
    gap: 32px;
    align-items: center;
    justify-content: center;
    margin-top: 28px;

    .page-arrows {
        cursor: pointer;
    }

    .page {
        color: #0076de;
        font-size: 20px;
        line-height: 28px;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .toggle {
        text-decoration: underline;
    }
}
</style>
